import React, { useState } from 'react';
import authService from '../../services/auth.service';
import './AuthPopup.css';

interface AuthPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (token: string, username: string) => void;
  onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
  onForgotPasswordClick?: () => void;
}

const AuthPopup: React.FC<AuthPopupProps> = ({ isOpen, onClose, onLoginSuccess, onShowToast, onForgotPasswordClick }) => {
  console.log('[AuthPopup] Component rendered with onLoginSuccess:', typeof onLoginSuccess);
  
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.login(loginData);
      console.log('[AuthPopup] Login response:', response);
      
      if (response.success && response.accessToken && response.username) {
        console.log('[AuthPopup] Login successful, calling onLoginSuccess callback...');
        
        // Save token first
        authService.setAccessToken(response.accessToken);
        console.log('[AuthPopup] Token saved to cookie');
        
        // Call parent callback
        onLoginSuccess(response.accessToken, response.username);
        console.log('[AuthPopup] onLoginSuccess callback executed');
        
        // Close popup after callback
        onClose();
        console.log('[AuthPopup] Popup closed');
      } else {
        setError(response.message || 'Đăng nhập thất bại');
      }
    } catch (err) {
      console.error('[AuthPopup] Login error:', err);
      setError('Có lỗi xảy ra khi đăng nhập');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.register(registerData);
      
      if (response.success) {
        // Hiển thị thông báo thành công và chuyển về tab đăng nhập
        onShowToast('Đăng ký thành công! Vui lòng đăng nhập.', 'success');
        setActiveTab('login');
        setRegisterData({ username: '', email: '', password: '' });
      } else {
        setError(response.message || 'Đăng ký thất bại');
      }
    } catch (err) {
      setError('Có lỗi xảy ra khi đăng ký');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setLoginData({ username: '', password: '' });
    setRegisterData({ username: '', email: '', password: '' });
    setError('');
  };

  const handleTabChange = (tab: 'login' | 'register') => {
    setActiveTab(tab);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="auth-popup-overlay" onClick={onClose}>
      <div className="auth-popup" onClick={(e) => e.stopPropagation()}>
        <div className="auth-popup-header">
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="auth-tabs">
          <button 
            className={`tab-btn ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabChange('login')}
          >
            Đăng Nhập
          </button>
          <button 
            className={`tab-btn ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => handleTabChange('register')}
          >
            Đăng Ký
          </button>
        </div>

        <div className="auth-content">
          {activeTab === 'login' ? (
            <form onSubmit={handleLogin} className="auth-form">
              <div className="form-group">
                <label htmlFor="login-username">Tên đăng nhập</label>
                <input
                  type="text"
                  id="login-username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="login-password">Mật khẩu</label>
                <input
                  type="password"
                  id="login-password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Đang xử lý...' : 'Đăng Nhập'}
              </button>

              <button 
                type="button" 
                className="forgot-password-btn"
                onClick={() => onForgotPasswordClick?.()}
              >
                Quên mật khẩu?
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="auth-form">
              <div className="form-group">
                <label htmlFor="register-username">Tên đăng nhập</label>
                <input
                  type="text"
                  id="register-username"
                  value={registerData.username}
                  onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="register-email">Email</label>
                <input
                  type="email"
                  id="register-email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="register-password">Mật khẩu</label>
                <input
                  type="password"
                  id="register-password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Đang xử lý...' : 'Đăng Ký'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPopup;
